import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertTriangle, LogIn } from "lucide-react";
import { toast } from "sonner";

const Login = () => {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [loginData, setLoginData] = useState({ username: "", password: "", property_id: "" });
  const [userProperties, setUserProperties] = useState([]);

  // Property options
  const PROPERTIES = [
    { id: "GTHR", name: "Golden Tulip Holland Resort" },
    { id: "GH", name: "Golden Hill" }
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.username, loginData.password, loginData.property_id);
      toast.success("Welcome to ORBIT");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 grid-pattern opacity-30" />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
      
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-sm mb-4 gold-glow">
            <AlertTriangle className="w-10 h-10 text-primary" />
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-primary uppercase gold-glow-text">
            ORBIT
          </h1>
          <p className="text-muted-foreground text-sm tracking-widest uppercase mt-1">
            Operational Risk & Business Intelligence Tracker
          </p>
        </div>

        <Card className="orbit-card border-border/50 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <CardTitle className="font-heading text-lg uppercase tracking-wide">
              Access Control
            </CardTitle>
            <CardDescription className="text-xs">
              Golden Tulip Holland Resort Batu
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-username" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Username
                </Label>
                <Input
                  id="login-username"
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  placeholder="username"
                  required
                  autoCapitalize="none"
                  className="bg-background border-border focus:border-primary"
                  data-testid="login-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Password
                </Label>
                <Input
                  id="login-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  className="bg-background border-border focus:border-primary"
                  data-testid="login-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-property" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Property
                </Label>
                <Select
                  value={loginData.property_id}
                  onValueChange={(v) => setLoginData({ ...loginData, property_id: v })}
                >
                  <SelectTrigger className="bg-background border-border" data-testid="login-property">
                    <SelectValue placeholder="Pilih Property" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTIES.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
                data-testid="login-submit"
              >
                <LogIn size={16} className="mr-2" />
                {loading ? "Authenticating..." : "Access System"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          © 2026 Golden Tulip Holland Resort Batu - Created by LuckyTech
        </p>
      </div>
    </div>
  );
};

export default Login;
